import React, { useState, useEffect } from 'react';
import { Calendar, Check, X, Copy, CheckIcon, FileText, Phone, Mail, Search, User, CalendarRange, Users, Clock, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useMsal } from "@azure/msal-react";
import { getGraphClient } from '../lib/msGraph';

interface Meeting {
  id: string;
  title: string;
  first_name: string;
  last_name: string;
  date: string;
  duration: number;
  location: string;
  confirmed: boolean | null;
  created_by: string;
  created_by_email?: string;
  phone_number?: string;
  attendees?: string[];
  calendar_clicks: Array<{ provider: string; count: number }>;
  calendar_emails?: Array<{ email: string }>;
  meeting_type: string;
  meeting_category: string;
  notes?: string;
  meeting_description?: string;
  microsoft_event_id?: string;
}

interface UserFilter {
  id: string;
  email: string;
}

export default function MeetingsOverview() {
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [users, setUsers] = useState<UserFilter[]>([]);
  const [selectedUser, setSelectedUser] = useState<string>('all');
  const { user } = useAuth();
  const [typeFilter, setTypeFilter] = useState('all');
  const { accounts } = useMsal();
  const [deletingMeeting, setDeletingMeeting] = useState<string | null>(null);

  // Search filters
  const [searchTerm, setSearchTerm] = useState('');
  
  // Date filters
  const [meetingDateStart, setMeetingDateStart] = useState('');
  const [meetingDateEnd, setMeetingDateEnd] = useState('');
  const [createdDateStart, setCreatedDateStart] = useState('');
  const [createdDateEnd, setCreatedDateEnd] = useState('');

  const meetingTypes = {
    phone_interview: 'Telefoninterview mit TOK Rec2Rec',
    briefing: 'Rücksprache/ Briefing mit TOK Rec2Rec',
    p1: 'P1 - Vorstellungsgespräch',
    p2: 'P2 - Vorstellungsgespräch',
    p3: 'P3 - Vorstellungsgespräch',
    p4: 'P4 - Vorstellungsgespräch',
    p5: 'P5 - Vorstellungsgespräch',
    contract: 'Vertragsgespräch',
    other: 'sonstiges'
  };

  const meetingCategories = {
    phone: 'Telefon',
    video: 'Video-Call',
    inperson: 'persönlich vor Ort'
  };

  useEffect(() => {
    document.title = 'Terminübersicht | TOK rec2rec Terminplaner';
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Load users for filter
        const { data: userData, error: userError } = await supabase
          .from('user_profiles')
          .select('id, email')
          .order('email');

        if (userError) throw userError;
        setUsers(userData || []);

        // Load meetings with all information
        let query = supabase
          .from('meetings')
          .select(`
            *,
            user_profiles!meetings_created_by_fkey (
              email
            ),
            calendar_clicks (
              provider
            ),
            calendar_emails (
              email
            )
          `)
          .gte('date', new Date().toISOString()) // Only fetch current and future meetings
          .order('date', { ascending: true });

        // Apply filters
        if (selectedUser !== 'all') {
          query = query.eq('created_by', selectedUser);
        }

        if (meetingDateStart) {
          query = query.gte('date', meetingDateStart);
        }
        if (meetingDateEnd) {
          query = query.lte('date', meetingDateEnd);
        }
        if (createdDateStart) {
          query = query.gte('created_at', createdDateStart);
        }
        if (createdDateEnd) {
          query = query.lte('created_at', createdDateEnd);
        }

        const { data: meetingsData, error: meetingsError } = await query;

        if (meetingsError) throw meetingsError;

        const processedMeetings = (meetingsData || []).map(meeting => ({
          ...meeting,
          created_by_email: meeting.user_profiles?.email,
          calendar_clicks: Object.entries(
            (meeting.calendar_clicks || []).reduce((acc: any, click: any) => {
              acc[click.provider] = (acc[click.provider] || 0) + 1;
              return acc;
            }, {})
          ).map(([provider, count]) => ({ provider, count }))
        }));

        setMeetings(processedMeetings);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Fehler beim Laden der Daten');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedUser, meetingDateStart, meetingDateEnd, createdDateStart, createdDateEnd]);

  const handleDeleteMeeting = async (meetingId: string) => {
    if (!window.confirm('Sind Sie sicher, dass Sie diesen Termin löschen möchten?')) {
      return;
    }

    setDeletingMeeting(meetingId);
    try {
      // Finde den Termin
      const { data: meeting, error: fetchError } = await supabase
        .from('meetings')
        .select('*')
        .eq('id', meetingId)
        .single();

      if (fetchError) throw fetchError;

      // Prüfe, ob der aktuelle Benutzer der Ersteller ist
      if (meeting.created_by !== user?.id) {
        toast.error('Sie können nur Ihre eigenen Termine löschen');
        return;
      }

      // Wenn Microsoft-Konto verbunden ist und ein Event-ID existiert, lösche den Termin aus dem Kalender
      if (accounts[0] && meeting.microsoft_event_id) {
        try {
          const graphClient = getGraphClient();
          await graphClient
            .api(`/me/events/${meeting.microsoft_event_id}`)
            .delete();
        } catch (error) {
          console.error('Error deleting Microsoft calendar event:', error);
          toast.error('Fehler beim Löschen des Kalendereintrags');
        }
      }

      // Lösche den Termin aus der Datenbank
      const { error: deleteError } = await supabase
        .from('meetings')
        .delete()
        .eq('id', meetingId);

      if (deleteError) throw deleteError;

      // Aktualisiere die Terminliste
      setMeetings(meetings.filter(m => m.id !== meetingId));
      toast.success('Termin erfolgreich gelöscht');
    } catch (error) {
      console.error('Error deleting meeting:', error);
      toast.error('Fehler beim Löschen des Termins');
    } finally {
      setDeletingMeeting(null);
    }
  };

  const copyToClipboard = async (id: string) => {
    const link = `${window.location.origin}/meeting/${id}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedId(id);
      toast.success('Link in die Zwischenablage kopiert!');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      toast.error('Fehler beim Kopieren des Links');
    }
  };

  const clearFilters = () => {
    setSelectedUser('all');
    setMeetingDateStart('');
    setMeetingDateEnd('');
    setCreatedDateStart('');
    setCreatedDateEnd('');
    setSearchTerm('');
    setTypeFilter('all');
  };

  const filteredMeetings = meetings.filter(meeting => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      meeting.title.toLowerCase().includes(searchLower) ||
      meeting.first_name.toLowerCase().includes(searchLower) ||
      meeting.last_name.toLowerCase().includes(searchLower) ||
      meeting.created_by_email?.toLowerCase().includes(searchLower) ||
      (meeting.phone_number && meeting.phone_number.toLowerCase().includes(searchLower))
    );
  }).filter(meeting => {
    if (typeFilter === 'all') return true;
    return meeting.meeting_type === typeFilter;
  });

  const renderMeetingCard = (meeting: Meeting) => (
    <div key={meeting.id} className="bg-white p-4 rounded-lg shadow border border-gray-200">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h4 className="text-lg font-medium text-gray-900">{meeting.title}</h4>
          <div className="mt-2 space-y-1">
            <p className="text-sm text-gray-500">
              mit {meeting.first_name} {meeting.last_name}
            </p>
            {meeting.phone_number && (
              <p className="text-sm text-gray-500 flex items-center">
                <Phone className="h-4 w-4 mr-2" />
                {meeting.phone_number}
              </p>
            )}
            <p className="text-sm text-gray-500 flex items-center">
              <Calendar className="h-4 w-4 mr-2" />
              {new Date(meeting.date).toLocaleDateString('de-DE')} um{' '}
              {new Date(meeting.date).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr
              ({meeting.duration} min)
            </p>
            {meeting.location && (
              <p className="text-sm text-gray-500">
                Ort: {meeting.location}
              </p>
            )}
            <p className="text-sm text-gray-500 flex items-center">
              <Mail className="h-4 w-4 mr-2" />
              Erstellt von: {meeting.created_by_email}
            </p>

            {/* Meeting Type and Category */}
            <div className="flex flex-wrap gap-2 mt-2">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {meetingTypes[meeting.meeting_type as keyof typeof meetingTypes] || meeting.meeting_type}
              </span>
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                {meetingCategories[meeting.meeting_category as keyof typeof meetingCategories] || meeting.meeting_category}
              </span>
            </div>

            {/* Public Description */}
            {meeting.meeting_description && (
              <div className="mt-3 bg-gray-50 p-3 rounded-md">
                <p className="text-sm font-medium text-gray-700 mb-1">Öffentliche Beschreibung:</p>
                <p className="text-sm text-gray-600">{meeting.meeting_description}</p>
              </div>
            )}

            {/* Internal Notes */}
            {meeting.notes && (
              <div className="mt-3 bg-yellow-50 p-3 rounded-md border border-yellow-200">
                <p className="text-sm font-medium text-yellow-800 mb-1">Interne Notizen:</p>
                <p className="text-sm text-yellow-700">{meeting.notes}</p>
              </div>
            )}

            {/* Attendees Section */}
            {meeting.attendees && meeting.attendees.length > 0 && (
              <div className="mt-3">
                <p className="text-sm font-medium text-gray-700 flex items-center">
                  <Users className="h-4 w-4 mr-2" />
                  Teilnehmer:
                </p>
                <div className="mt-1 space-y-1">
                  {meeting.attendees.map((email, index) => (
                    <p key={index} className="text-sm text-gray-500 flex items-center pl-6">
                      <Mail className="h-3 w-3 mr-2" />
                      {email}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {meeting.calendar_emails?.length > 0 && (
              <div className="mt-3">
                <p className="text-sm font-medium text-gray-700">Kalendereinladungen gesendet an:</p>
                <ul className="mt-1 space-y-1">
                  {meeting.calendar_emails.map((entry, index) => (
                    <li key={index} className="text-sm text-gray-500 flex items-center">
                      <Mail className="h-4 w-4 mr-2" />
                      {entry.email}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Calendar Clicks */}
            {meeting.calendar_clicks.length > 0 && (
              <div className="mt-3">
                <p className="text-sm font-medium text-gray-700">Kalender-Klicks:</p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {meeting.calendar_clicks.map(({ provider, count }) => (
                    <span
                      key={provider}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      <Calendar className="w-3 h-3 mr-1" />
                      {provider}: {count}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="ml-4 flex flex-col items-end space-y-2">
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              meeting.confirmed === true
                ? 'bg-green-100 text-green-800'
                : meeting.confirmed === false
                ? 'bg-red-100 text-red-800'
                : 'bg-yellow-100 text-yellow-800'
            }`}
          >
            {meeting.confirmed === true ? (
              <Check className="w-4 h-4 mr-1" />
            ) : meeting.confirmed === false ? (
              <X className="w-4 h-4 mr-1" />
            ) : (
              <Clock className="w-4 h-4 mr-1" />
            )}
            {meeting.confirmed === true
              ? 'Bestätigt'
              : meeting.confirmed === false
              ? 'Abgelehnt'
              : 'Nicht bestätigt'}
          </span>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => copyToClipboard(meeting.id)}
              className="p-1 border border-gray-300 rounded-md hover:bg-gray-50"
              title="Link kopieren"
            >
              {copiedId === meeting.id ? (
                <CheckIcon className="h-4 w-4 text-green-500" />
              ) : (
                <Copy className="h-4 w-4 text-gray-500" />
              )}
            </button>
            {meeting.created_by === user?.id && (
              <button
                onClick={() => handleDeleteMeeting(meeting.id)}
                disabled={deletingMeeting === meeting.id}
                className="p-1 border border-red-300 rounded-md hover:bg-red-50 text-red-600 disabled:opacity-50"
                title="Termin löschen"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  const unconfirmedMeetings = filteredMeetings.filter(m => m.confirmed === null);
  const confirmedMeetings = filteredMeetings.filter(m => m.confirmed === true);
  const rejectedMeetings = filteredMeetings.filter(m => m.confirmed === false);

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Terminübersicht</h2>
        
        {/* Filter Section */}
        <div className="bg-white p-4 rounded-lg border-2 border-gray-200 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* User Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Benutzer
              </label>
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="block w-full rounded-md border-2 border-gray-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
              >
                <option value="all">Alle Benutzer</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.email}
                  </option>
                ))}
              </select>
            </div>

            {/* Meeting Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Terminart
              </label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="mt-1 block w-full rounded-md border-2 border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200 focus:ring-opacity-50"
              >
                <option value="all">Alle</option>
                {Object.entries(meetingTypes).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            {/* Meeting Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Termindatum von-bis
              </label>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="date"
                  value={meetingDateStart}
                  onChange={(e) => setMeetingDateStart(e.target.value)}
                  className="block w-full rounded-md border-2 border-gray-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <input
                  type="date"
                  value={meetingDateEnd}
                  onChange={(e) => setMeetingDateEnd(e.target.value)}
                  className="block w-full rounded-md border-2 border-gray-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
              </div>
            </div>

            {/* Created Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Erstellungsdatum von-bis
              </label>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="date"
                  value={createdDateStart}
                  onChange={(e) => setCreatedDateStart(e.target.value)}
                  className="block w-full rounded-md border-2 border-gray-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <input
                  type="date"
                  value={createdDateEnd}
                  onChange={(e) => setCreatedDateEnd(e.target.value)}
                  className="block w-full rounded-md border-2 border-gray-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
              </div>
            </div>

            {/* Search Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Suche
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Name, Titel, E-Mail..."
                  className="block w-full rounded-md border-2 border-gray-300 pl-10 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              </div>
            </div>
          </div>

          {/* Clear Filters Button */}
          <div className="mt-4">
            <button
              onClick={clearFilters}
              className="w-full sm:w-auto px-4 py-2 border-2 border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Filter zurücksetzen
            </button>
          </div>
        </div>

        {/* Results count */}
        <div className="text-sm text-gray-500 mb-4">
          {filteredMeetings.length} Termine gefunden
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Nicht bestätigte Termine */}
        <div className="bg-gray-50 p-4 rounded-lg border-2 border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Clock className="w-5 h-5 mr-2 text-yellow-500" />
            Nicht bestätigt ({unconfirmedMeetings.length})
          </h3>
          <div className="space-y-4">
            {unconfirmedMeetings.map(renderMeetingCard)}
            {unconfirmedMeetings.length === 0 && (
              <p className="text-center text-gray-500 py-4">
                Keine unbestätigten Termine
              </p>
            )}
          </div>
        </div>

        {/* Bestätigte Termine */}
        <div className="bg-gray-50 p-4 rounded-lg border-2 border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Check className="w-5 h-5 mr-2 text-green-500" />
            Bestätigt ({confirmedMeetings.length})
          </h3>
          <div className="space-y-4">
            {confirmedMeetings.map(renderMeetingCard)}
            {confirmedMeetings.length === 0 && (
              <p className="text-center text-gray-500 py-4">
                Keine bestätigten Termine
              </p>
            )}
          </div>
        </div>

        {/* Abgelehnte Termine */}
        <div className="bg-gray-50 p-4 rounded-lg border-2 border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <X className="w-5 h-5 mr-2 text-red-500" />
            Abgelehnt ({rejectedMeetings.length})
          </h3>
          <div className="space-y-4">
            {rejectedMeetings.map(renderMeetingCard)}
            {rejectedMeetings.length === 0 && (
              <p className="text-center text-gray-500 py-4">
                Keine abgelehnten Termine
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}